import React from 'react';

import { Button, Group, LoadingOverlay, Modal, Stack, Text, Title } from '@mantine/core';
import useAppointment from 'hooks/appointment/useAppointment';
import useDeleteAppointment from 'hooks/appointment/useDeleteAppointment';
import notificationHandler from 'utils/notifications';

interface IModalAppointmentDeleteProps {
  opened: boolean;
  handleCloseModal: () => void;
  handleCloseDrawer: () => void;
  appointmentId?: string;
  setAppointmentId: (appointmentId: string | undefined) => void;
}

const ModalAppointmentDelete = ({
  opened,
  appointmentId,
  handleCloseModal,
  handleCloseDrawer,
  setAppointmentId,
}: IModalAppointmentDeleteProps): JSX.Element => {
  const deleteAppointment = useDeleteAppointment();
  const { data, isLoading } = useAppointment(appointmentId);

  const { successNotification, errorNotification } = notificationHandler();
  const handleDelete = (): void => {
    if (data) {
      deleteAppointment.mutate(data.data.appointmentId, {
        onSuccess: () => {
          setAppointmentId(undefined);
          successNotification(
            'Appointment delete success',
            `This appointment has been successfully deleted.`,
          );
          handleCloseModal();
          handleCloseDrawer();
        },
        onError: error => {
          errorNotification('Appointment delete failed', error.response?.data.message);
        },
      });
    }
  };
  return (
    <Modal
      centered
      trapFocus
      opened={opened}
      onClose={handleCloseModal}
      title={<Title order={4}>Delete Appointment</Title>}
      size='auto'
      data-cy='deleteAppointmentModal'
    >
      <LoadingOverlay visible={isLoading} />
      <Stack>
        <Text>{`Are you sure you want to delete ${data?.data.appointmentType.name}?`}</Text>
        {data?.data.athleteAppointments.some(aa => aa.isLateCancel || aa.isNoShow) && (
          <Text color='orange'>
            {`Important: ${
              data.data.athleteAppointments.some(aa => aa.isLateCancel) &&
              data.data.athleteAppointments.some(aa => aa.isNoShow)
                ? 'Late cancellation and no-show fees apply'
                : data.data.athleteAppointments.some(aa => aa.isLateCancel)
                ? 'A late cancellation fee applies'
                : 'A no-show fee applies'
            }. An invoice will still be generated and linked to this appointment after deletion.`}
          </Text>
        )}
        <Text color='dimmed'>This action cannot be undone.</Text>
        <Group position='right'>
          <Button
            onClick={handleDelete}
            color='red'
            loading={deleteAppointment.isLoading}
            disabled={isLoading}
            data-cy='deleteAppointmentBtn'
          >
            Delete
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ModalAppointmentDelete;
